<template>
  <div class="upload-case-container">
    <div class="upload-case-wrapper">
      <!-- 页面头部 -->
      <div class="page-header">
        <div class="header-left">
          <el-button
            type="text"
            icon="el-icon-arrow-left"
            class="back-btn"
            @click="goBack"
          >
            返回
          </el-button>
          <h2 class="page-title">上传案例</h2>
        </div>
      </div>

      <!-- 上传表单 -->
      <div class="upload-form">
        <el-form
          ref="uploadForm"
          :model="formData"
          :rules="formRules"
          label-width="120px"
          class="case-form"
        >
          <!-- 案例标题 -->
          <el-form-item label="案例标题" prop="title">
            <el-input
              v-model="formData.title"
              placeholder="请输入案例标题"
              maxlength="100"
              show-word-limit
            />
          </el-form-item>

          <!-- 案例分类 -->
          <el-form-item label="案例分类" prop="category_id">
            <el-select
              v-model="formData.category_id"
              placeholder="请选择案例分类"
              style="width: 100%"
            >
              <el-option
                v-for="category in categories"
                :key="category.id"
                :label="category.title"
                :value="category.id"
              />
            </el-select>
          </el-form-item>

          <!-- 项目地址 -->
          <el-form-item label="项目地址" prop="address">
            <el-input
              v-model="formData.address"
              placeholder="请输入项目地址"
              maxlength="200"
            />
          </el-form-item>

          <!-- 选择省份 -->
          <el-form-item label="选择省份" prop="region">
            <el-select
              v-model="formData.region"
              placeholder="请选择省份"
              style="width: 100%"
              filterable
            >
              <el-option
                v-for="province in provinces"
                :key="province"
                :label="province"
                :value="province"
              />
            </el-select>
          </el-form-item>

          <!-- 项目规模 -->
          <el-form-item label="项目规模" prop="scale">
            <el-input
              v-model="formData.scale"
              placeholder="请输入项目规模，如：小型项目、中型项目、大型项目、特大型项目"
              maxlength="50"
            />
          </el-form-item>

          <!-- 结构体系 -->
          <el-form-item label="结构体系" prop="structure_system">
            <el-input
              v-model="formData.structure_system"
              placeholder="请输入结构体系"
              maxlength="100"
            />
          </el-form-item>

          <!-- 造价指标 -->
          <el-form-item label="造价指标" prop="cost_indicators">
            <el-input
              v-model="formData.cost_indicators"
              type="textarea"
              :rows="3"
              placeholder="请输入造价指标，如：总投资约5亿元，建筑面积10万平方米"
              maxlength="500"
              show-word-limit
            />
          </el-form-item>

          <!-- 项目描述 -->
          <el-form-item label="项目描述" prop="project_description">
            <el-input
              v-model="formData.project_description"
              type="textarea"
              :rows="4"
              placeholder="请输入项目描述"
              maxlength="1000"
              show-word-limit
            />
          </el-form-item>

          <!-- 其他指标 -->
          <el-form-item label="其他指标" prop="other_indicators">
            <el-input
              v-model="formData.other_indicators"
              type="textarea"
              :rows="3"
              placeholder="请输入其他指标，如：建筑高度120米，地下3层，地上30层"
              maxlength="500"
              show-word-limit
            />
          </el-form-item>

          <!-- 案例图片 -->
          <el-form-item label="案例图片" prop="images">
            <div class="image-upload-section">
              <div class="upload-tip">最多可上传9张图片，支持jpg、png格式</div>
              <div class="image-list">
                <div
                  v-for="(image, index) in formData.images"
                  :key="index"
                  class="image-item"
                >
                  <img :src="getImageUrl(image)" :alt="`案例图片${index + 1}`" class="uploaded-image">
                  <div class="image-overlay">
                    <el-button
                      type="danger"
                      size="mini"
                      icon="el-icon-delete"
                      circle
                      @click="removeImage(index)"
                    />
                  </div>
                </div>
                <div
                  v-if="formData.images.length < 9"
                  class="upload-placeholder"
                  @click="uploadImage"
                >
                  <i class="el-icon-plus upload-icon" />
                  <div class="upload-text">上传图片</div>
                </div>
              </div>
            </div>
          </el-form-item>

          <!-- 数据文件 -->
          <el-form-item label="数据文件" prop="data_table_file">
            <div class="file-upload-section">
              <div v-if="formData.data_table_file" class="file-info">
                <i class="el-icon-document file-icon" />
                <span class="file-name">{{ getFileName(formData.data_table_file) }}</span>
                <el-button
                  type="danger"
                  size="mini"
                  icon="el-icon-delete"
                  @click="removeFile"
                >
                  删除
                </el-button>
              </div>
              <div v-else class="file-upload-area" @click="uploadFile">
                <i class="el-icon-upload file-upload-icon" />
                <div class="file-upload-text">点击上传压缩包</div>
                <div class="file-upload-tip">支持.zip、.rar格式</div>
              </div>
            </div>
          </el-form-item>

          <!-- 模版案例 -->
          <el-form-item label="模版案例" prop="template_file">
            <div class="file-upload-section">
              <div v-if="formData.template_file" class="file-info">
                <i class="el-icon-document file-icon" />
                <span class="file-name">{{ getFileName(formData.template_file) }}</span>
                <el-button
                  type="danger"
                  size="mini"
                  icon="el-icon-delete"
                  @click="removeTemplateFile"
                >
                  删除
                </el-button>
              </div>
              <div v-else class="file-upload-area" @click="uploadTemplateFile">
                <i class="el-icon-upload file-upload-icon" />
                <div class="file-upload-text">点击上传模版案例</div>
                <div class="file-upload-tip">支持.doc、.docx、.xls、.xlsx、.pdf格式</div>
              </div>
            </div>
          </el-form-item>

          <!-- 收费设置 -->
          <el-form-item label="收费设置">
            <div class="charge-setting" style="height: 40px;">
              <el-radio-group v-model="formData.is_free">
                <el-radio :label="1">免费</el-radio>
                <el-radio :label="0">收费</el-radio>
              </el-radio-group>
              <el-input
                v-if="formData.is_free === 0"
                v-model="formData.price"
                type="number"
                placeholder="请输入价格"
                style="width: 200px; margin-left: 20px;"
              >
                <template slot="append">元</template>
              </el-input>
            </div>
          </el-form-item>

          <!-- 提交按钮 -->
          <el-form-item>
            <div class="form-actions">
              <el-button @click="goBack">取消</el-button>
              <el-button
                type="primary"
                :loading="submitting"
                @click="submitForm"
              >
                {{ submitting ? '上传中...' : '提交案例' }}
              </el-button>
            </div>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadCostCase, getCostCaseCategory, uploadImage } from '@/api/user'

export default {
  name: 'UploadCase',
  data() {
    return {
      submitting: false,
      categories: [],
      provinces: [
        '北京市', '天津市', '上海市', '重庆市',
        '河北省', '山西省', '辽宁省', '吉林省', '黑龙江省',
        '江苏省', '浙江省', '安徽省', '福建省', '江西省', '山东省',
        '河南省', '湖北省', '湖南省', '广东省', '海南省',
        '四川省', '贵州省', '云南省', '陕西省', '甘肃省', '青海省',
        '台湾省',
        '内蒙古自治区', '广西壮族自治区', '西藏自治区', '宁夏回族自治区', '新疆维吾尔自治区',
        '香港特别行政区', '澳门特别行政区'
      ],
      formData: {
        title: '',
        category_id: null,
        address: '',
        region: '',
        scale: '',
        structure_system: '',
        cost_indicators: '',
        project_description: '',
        other_indicators: '',
        images: [],
        data_table_file: '',
        template_file: '',
        is_free: 1,
        price: 0
      },
      formRules: {
        title: [
          { required: true, message: '请输入案例标题', trigger: 'blur' },
          { min: 2, max: 100, message: '标题长度在2到100个字符', trigger: 'blur' }
        ],
        category_id: [
          { required: true, message: '请选择案例分类', trigger: 'change' }
        ],
        address: [
          { required: true, message: '请输入项目地址', trigger: 'blur' }
        ],
        region: [
          { required: true, message: '请选择省份', trigger: 'change' }
        ],
        scale: [
          { required: true, message: '请输入项目规模', trigger: 'blur' }
        ],
        structure_system: [
          { required: true, message: '请输入结构体系', trigger: 'blur' }
        ],
        cost_indicators: [
          { required: true, message: '请输入造价指标', trigger: 'blur' }
        ],
        project_description: [
          { required: true, message: '请输入项目描述', trigger: 'blur' }
        ]
      }
    }
  },
  async mounted() {
    await this.fetchCategories()
  },
  methods: {
    // 返回上一页
    goBack() {
      this.$router.go(-1)
    },

    // 获取案例分类
    async fetchCategories() {
      try {
        const response = await getCostCaseCategory()
        if (response && response.code === 1 && response.data) {
          // 扁平化所有分类，包括子分类
          this.categories = this.flattenCategories(response.data)
        }
      } catch (error) {
        console.error('获取案例分类失败:', error)
        this.$message.error('获取案例分类失败')
      }
    },

    // 扁平化分类数据，获取所有可选的分类
    flattenCategories(categories) {
      const result = []

      const flatten = (items, level = 0) => {
        items.forEach(item => {
          // 添加当前分类
          result.push({
            id: item.id,
            title: '　'.repeat(level) + item.title, // 使用空格缩进表示层级
            pid: item.pid,
            has_children: item.has_children
          })

          // 如果有子分类，递归处理
          if (item.children && item.children.length > 0) {
            flatten(item.children, level + 1)
          }
        })
      }

      flatten(categories)
      return result
    },

    // 上传图片
    uploadImage() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.multiple = true
      input.onchange = async(e) => {
        const files = Array.from(e.target.files)
        if (files.length === 0) return

        // 检查文件数量限制
        if (this.formData.images.length + files.length > 9) {
          this.$message.warning('最多只能上传9张图片')
          return
        }

        // 检查文件格式和大小
        for (const file of files) {
          if (!file.type.startsWith('image/')) {
            this.$message.warning('请选择图片文件')
            return
          }
          if (file.size > 5 * 1024 * 1024) {
            this.$message.warning('图片大小不能超过5MB')
            return
          }
        }

        // 上传文件
        for (const file of files) {
          try {
            const response = await uploadImage(file)
            if (response && response.code === 1 && response.data) {
              this.formData.images.push(response.data.url)
            } else {
              this.$message.error('图片上传失败')
            }
          } catch (error) {
            console.error('图片上传失败:', error)
            this.$message.error('图片上传失败')
          }
        }
      }
      input.click()
    },

    // 删除图片
    removeImage(index) {
      this.formData.images.splice(index, 1)
    },

    // 上传文件
    uploadFile() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.zip,.rar'
      input.onchange = async(e) => {
        const file = e.target.files[0]
        if (!file) return

        // 检查文件格式
        const allowedTypes = [
          'application/zip',
          'application/x-rar-compressed',
          'application/vnd.rar'
        ]
        const fileExtension = file.name.toLowerCase().split('.').pop()
        if (!allowedTypes.includes(file.type) && !['zip', 'rar'].includes(fileExtension)) {
          this.$message.warning('请选择zip或rar压缩包文件')
          return
        }

        // 检查文件大小
        if (file.size > 50 * 1024 * 1024) {
          this.$message.warning('文件大小不能超过50MB')
          return
        }

        try {
          const response = await uploadImage(file)
          if (response && response.code === 1 && response.data) {
            this.formData.data_table_file = response.data.url
            this.$message.success('压缩包上传成功')
          } else {
            this.$message.error('文件上传失败')
          }
        } catch (error) {
          console.error('文件上传失败:', error)
          this.$message.error('文件上传失败')
        }
      }
      input.click()
    },

    // 删除文件
    removeFile() {
      this.formData.data_table_file = ''
    },

    // 上传模版文件
    uploadTemplateFile() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.doc,.docx,.xls,.xlsx,.pdf'
      input.onchange = async(e) => {
        const file = e.target.files[0]
        if (!file) return

        // 检查文件格式
        const allowedTypes = [
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/pdf'
        ]
        const fileExtension = file.name.toLowerCase().split('.').pop()
        const allowedExtensions = ['doc', 'docx', 'xls', 'xlsx', 'pdf']
        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
          this.$message.warning('请选择.doc、.docx、.xls、.xlsx或.pdf格式的文件')
          return
        }

        // 检查文件大小
        if (file.size > 50 * 1024 * 1024) {
          this.$message.warning('文件大小不能超过50MB')
          return
        }

        try {
          const response = await uploadImage(file)
          if (response && response.code === 1 && response.data) {
            this.formData.template_file = response.data.url
            this.$message.success('模版案例上传成功')
          } else {
            this.$message.error('文件上传失败')
          }
        } catch (error) {
          console.error('文件上传失败:', error)
          this.$message.error('文件上传失败')
        }
      }
      input.click()
    },

    // 删除模版文件
    removeTemplateFile() {
      this.formData.template_file = ''
    },

    // 获取文件名
    getFileName(filePath) {
      if (!filePath) return ''
      return filePath.split('/').pop()
    },

    // 获取图片URL
    getImageUrl(imagePath) {
      if (!imagePath) return ''
      if (imagePath.startsWith('http')) return imagePath
      return `${process.env.VUE_APP_BASE_API}${imagePath}`
    },

    // 提交表单
    async submitForm() {
      try {
        const valid = await this.$refs.uploadForm.validate()
        if (!valid) return

        this.submitting = true

        const response = await uploadCostCase({
          title: this.formData.title,
          category_id: this.formData.category_id,
          address: this.formData.address,
          region: this.formData.region,
          scale: this.formData.scale,
          structure_system: this.formData.structure_system,
          cost_indicators: this.formData.cost_indicators,
          project_description: this.formData.project_description,
          other_indicators: this.formData.other_indicators,
          images: this.formData.images,
          data_table_file: this.formData.data_table_file,
          template_file: this.formData.template_file,
          is_free: this.formData.is_free,
          price: this.formData.price
        })

        if (response && response.code === 1) {
          this.$message.success('案例上传成功')
          // 返回案例管理页面并刷新列表
          this.$router.push({
            path: '/profile/index',
            query: { refreshCases: true }
          })
        } else {
          this.$message.error(response?.msg || '案例上传失败')
        }
      } catch (error) {
        console.error('案例上传失败:', error)
        this.$message.error('案例上传失败，请重试')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.upload-case-container {
  min-height: calc(100vh - var(--navbar-total-height, 80px));
  min-height: calc(100dvh - var(--navbar-total-height, 80px));
  background: #F5F5F5;
  padding: 20px;
}

.upload-case-wrapper {
  max-width: 800px;
  margin: 0 auto;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
}

.page-header {
  padding: 20px 24px;
  border-bottom: 1px solid #F2F3F5;
  background: #fff;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn {
  padding: 0;
  font-size: 16px;
  color: #606266;
}

.back-btn:hover {
  color: #1E80FF;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.upload-form {
  padding: 24px;
}

.case-form {
  max-width: 600px;
}

.image-upload-section {
  width: 100%;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-bottom: 12px;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.image-item {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-item:hover .image-overlay {
  opacity: 1;
}

.upload-placeholder {
  width: 100px;
  height: 100px;
  border: 2px dashed #E4E7ED;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #FAFAFA;
}

.upload-placeholder:hover {
  border-color: #1E80FF;
  background: #F0F7FF;
}

.upload-icon {
  font-size: 24px;
  color: #C0C4CC;
  margin-bottom: 4px;
}

.upload-text {
  font-size: 12px;
  color: #909399;
}

.file-upload-section {
  width: 100%;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #F5F7FA;
  border-radius: 8px;
}

.file-icon {
  font-size: 16px;
  color: #1E80FF;
}

.file-name {
  flex: 1;
  font-size: 14px;
  color: #303133;
}

.file-upload-area {
  padding: 20px;
  border: 2px dashed #E4E7ED;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #FAFAFA;
}

.file-upload-area:hover {
  border-color: #1E80FF;
  background: #F0F7FF;
}

.file-upload-icon {
  font-size: 32px;
  color: #C0C4CC;
  margin-bottom: 8px;
}

.file-upload-text {
  font-size: 14px;
  color: #606266;
  margin-bottom: 4px;
}

.file-upload-tip {
  font-size: 12px;
  color: #909399;
}

.charge-setting {
  display: flex;
  align-items: center;
  gap: 20px;
}

.charge-setting .el-radio-group {
  margin-top: 0;
}

.charge-setting .el-input {
  margin-top: 0;
}

.form-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 32px;
}

.form-actions .el-button {
  width: 120px;
  height: 40px;
}
</style>
